import {Component, type ErrorInfo, type ReactNode} from "react";


interface RouteErrorBoundaryProps {
    children: ReactNode;
}

interface RouteErrorBoundaryState {
    error: Error | null;
}

/** Сломавшийся динамический импорт — обычно старая вкладка после релиза: файлов с прежними именами уже нет. */
function isChunkLoadError(error: Error): boolean {
    const text = `${error.name} ${error.message}`.toLowerCase();
    return text.includes("dynamically imported module")
        || text.includes("chunkloaderror")
        || text.includes("failed to fetch");
}

const RELOAD_FLAG = "rl.chunk-reloaded";

/**
 * Ошибка при загрузке страницы не должна оставлять пользователя перед вечным
 * индикатором: Suspense ждёт ленивый чанк бесконечно, если импорт упал.
 * Для протухших чанков делаем одну автоматическую перезагрузку, для остальных
 * ошибок показываем экран с кнопкой.
 */
export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
    state: RouteErrorBoundaryState = {error: null};

    static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
        return {error};
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error("Route render failed:", error, info.componentStack);
        // Перезагружаемся один раз за сессию: если и после неё чанк не встал,
        // причина другая, и цикл перезагрузок только скрыл бы её.
        if (isChunkLoadError(error) && sessionStorage.getItem(RELOAD_FLAG) !== "1") {
            sessionStorage.setItem(RELOAD_FLAG, "1");
            window.location.reload();
        }
    }

    render() {
        if (!this.state.error) return this.props.children;
        return (
            <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-gradient-to-br from-red-50 via-amber-50 to-yellow-50 px-6 text-center">
                <h1 className="text-xl font-bold text-gray-800">Страница не загрузилась</h1>
                <p className="max-w-sm text-sm text-gray-600">
                    Что-то пошло не так при открытии раздела. Обновите страницу — если не помогло,
                    попробуйте позже.
                </p>
                <button
                    type="button"
                    onClick={() => {
                        sessionStorage.removeItem(RELOAD_FLAG);
                        window.location.reload();
                    }}
                    className="rla-btn rla-btn-cta"
                >
                    Обновить
                </button>
            </div>
        );
    }
}
